import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ChatService, Conversation } from '../services/chat.service';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

@Component({
  selector: 'app-chat-history',
  templateUrl: './chat-history.component.html',
  styleUrls: ['./chat-history.component.scss']
})
export class ChatHistoryComponent implements OnInit, OnDestroy {

  conversations: Conversation[] = [];
  selectedConversationId: number | null = null;
  isLoading = false;
  errorMessage = '';
  private destroy$ = new Subject<void>();

  constructor(
    private chatService: ChatService,
    private router: Router
  ) { }

  ngOnInit() {
    this.loadConversations();

    // Subscribe to conversations from service
    this.chatService.conversations$
      .pipe(takeUntil(this.destroy$))
      .subscribe(conversations => {
        this.conversations = conversations;
      });

    this.chatService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.isLoading = loading;
      });

    // Track selected conversation from URL
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateSelectedConversation();
      });

    // Set initial selected conversation
    this.updateSelectedConversation();
  }

  /**
   * Update selected conversation ID from current route
   */
  private updateSelectedConversation() {
    const urlSegments = this.router.url.split('/');
    const lastSegment = urlSegments[urlSegments.length - 1];
    const conversationId = parseInt(lastSegment, 10);

    if (!isNaN(conversationId)) {
      this.selectedConversationId = conversationId;
    } else {
      this.selectedConversationId = null;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load all conversations from API
   */
  loadConversations() {
    this.chatService.loadConversations();
  }

  /**
   * Select a conversation to view
   */
  selectConversation(conversationId: number) {
    // Navigate to the conversation URL - the route subscription will handle loading
    this.router.navigate(['/milo', conversationId]);
  }

  /**
   * Delete a conversation
   */
  deleteConversation(conversationId: number, event: Event) {
    event.stopPropagation();

    if (!confirm('Are you sure you want to delete this conversation?')) {
      return;
    }

    this.chatService.deleteConversation(conversationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.conversations = this.conversations.filter(c => c.id !== conversationId);
        },
        error: (error) => {
          this.errorMessage = 'Failed to delete conversation. Please try again.';
          console.error('Error deleting conversation:', error);
        }
      });
  }

  /**
   * Update conversation title
   */
  updateConversationTitle(conversationId: number, newTitle: string, event: Event) {
    event.stopPropagation();

    if (!newTitle.trim()) {
      return;
    }

    this.chatService.updateConversation(conversationId, newTitle)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const conversation = this.conversations.find(c => c.id === conversationId);
          if (conversation) {
            conversation.title = newTitle;
          }
        },
        error: (error) => {
          this.errorMessage = 'Failed to update conversation title. Please try again.';
          console.error('Error updating conversation:', error);
        }
      });
  }

  /**
   * Create a new conversation
   */
  createNewConversation() {
    // Navigate to base chat URL (will show welcome message)
    this.router.navigate(['/milo']);
  }

  /**
   * Get the preview text from the last message
   */
  getConversationPreview(conversation: Conversation): string {
    if (conversation.last_message) {
      const preview = conversation.last_message.content.substring(0, 100);
      return preview + (conversation.last_message.content.length > 100 ? '...' : '');
    }
    return 'No messages yet';
  }

  /**
   * Format timestamp to relative time
   */
  getTimeAgo(dateString: string): string {
    try {
      const date = new Date(dateString);
      const now = new Date();

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Recently';
      }

      const diff = now.getTime() - date.getTime();

      // Ensure diff is valid and positive
      if (isNaN(diff) || diff < 0) {
        return 'Recently';
      }

      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));

      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days === 0) return 'Today';
      if (days === 1) return 'Yesterday';
      if (days < 7) return `${days}d ago`;
      if (days < 30) return `${Math.floor(days / 7)}w ago`;
      return `${Math.floor(days / 30)}mo ago`;
    } catch (e) {
      return 'Recently';
    }
  }

}
